import api from './api';

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  userType: string;
  status: string;
  profilePictureUrl?: string;
  dateOfBirth?: string;
  schoolName?: string;
  gradeLevel?: string;
  timezone?: string;
  locale?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  birthYear?: number;
  hasPassword: boolean;
  googleLinked?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  timezone?: string;
  locale?: string;
  profilePictureUrl?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function extractError(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || err.message || fallback;
}

class ProfileService {
  async getProfile(): Promise<UserProfile> {
    try {
      const response = await api.get<ApiResponse<UserProfile>>('/api/users/me');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to load profile');
      }
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(extractError(error, 'Failed to load profile'));
    }
  }

  async updateProfile(payload: UpdateProfileRequest): Promise<UserProfile> {
    try {
      const response = await api.put<ApiResponse<UserProfile>>('/api/users/me', payload);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to update profile');
      }
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(extractError(error, 'Failed to update profile'));
    }
  }

  async setPasswordWithLoginCode(code: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      const response = await api.put<ApiResponse<string>>('/api/users/me/password/with-login-code', {
        code,
        newPassword,
        confirmPassword,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to set password');
      }
    } catch (error: unknown) {
      throw new Error(extractError(error, 'Failed to set password'));
    }
  }

  async changePassword(payload: ChangePasswordRequest): Promise<void> {
    try {
      const response = await api.put<ApiResponse<string>>('/api/users/me/password', payload);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error: unknown) {
      throw new Error(extractError(error, 'Failed to change password'));
    }
  }

  async forgotPassword(email: string): Promise<string> {
    const response = await api.post<ApiResponse<string>>('/api/users/forgot-password', { email });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send reset email');
    }
    return response.data.message;
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<void> {
    const response = await api.post<ApiResponse<string>>('/api/users/reset-password', {
      token,
      newPassword,
      confirmPassword,
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reset password');
    }
  }
}

export const profileService = new ProfileService();
export default profileService;
